import React, { useEffect, useRef, useState } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { Avatar, Button } from './ui';

// Avatar preview with upload/remove controls. `onSelect(file)` receives the
// chosen image; `onRemove()` clears it. The preview is local until saved.
export default function PhotoPicker({ src, name, color, onSelect, onRemove, busy = false, size = 'xl', square = false }) {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('errors.photoType'));
      return;
    }
    setError('');
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    onSelect(file);
  };

  const handleRemove = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setError('');
    onRemove();
  };

  const shown = preview || src;
  return (
    <div className="flex items-center gap-4">
      <Avatar src={shown} name={name} color={color} size={size} square={square} />
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
            <Camera size={14} />{shown ? t('photo.change') : t('photo.upload')}
          </Button>
          {shown && (
            <Button type="button" variant="ghost" size="sm" onClick={handleRemove} disabled={busy}>
              <Trash2 size={14} />{t('photo.remove')}
            </Button>
          )}
        </div>
        <p className={`text-xs mt-1.5 ${error ? 'text-red-600' : 'text-gray-400'}`}>{error || t('photo.help')}</p>
        <input ref={inputRef} type="file" accept="image/*" className="sr-only" aria-label={t('photo.upload')} onChange={handleFile} />
      </div>
    </div>
  );
}
